import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { minutesToHHMM } from "../../utils/minutesToHHMM";
import { useContext, useEffect, useState } from "react";
import { SectionData } from "../../services/PlansService";
import { toArrivalTime } from "../../utils/toArrivalTime";
import { convertDistance, getBikePace, getPace } from "../../utils/UnitsUtils";
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import styled from "@mui/material/styles/styled";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import { AppContext } from "../../contexts/AppContext";

const ParagraphLine = styled("span")(({ theme }) => ({
    verticalAlign: "baseline",
    height: 1,
    flexGrow: 1,
    flexShrink: 1,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginLeft: "0.5em",
    marginRight: "0.5em"
}));

const SmallTextField = styled(TextField)(({ theme }) => ({
    maxWidth: theme.spacing(6),
    "& input": {
        fontSize: "0.875rem"
    }
}));

const PlannedTimeBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    marginTop: theme.spacing(0),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CuttoffTimeBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    marginTop: theme.spacing(0),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

export interface TimeBoxProps {
    section: SectionData;
    hours?: number;
    minutes?: number;
    onChangeSectionTime: (minutes: number) => void;
    onChangeCutoff: (cutOff: Date | null) => void;
    startDate: Date;
    totalTimeBefore: number;
    totalTime: number;
    totalDistance: number;
    sport: "run" | "bike";
    scenarioPercentage?: number;
    scenarioLocked?: boolean;
    scenarioLabel?: string;
    onScenarioPercentageChange?: (percentage: number) => void;
    onScenarioLockToggle?: (locked: boolean) => void;
    readOnly?: boolean;
}

const TimeBox: React.FC<TimeBoxProps> = observer(({ section, hours, minutes, onChangeSectionTime, onChangeCutoff, startDate, totalTime, totalTimeBefore, totalDistance, sport, scenarioPercentage, scenarioLocked, scenarioLabel, onScenarioPercentageChange, onScenarioLockToggle, readOnly }) => {

    const { userService } = useContext(AppContext);
    const intl = useIntl();

    const distanceUnits = userService.user?.distanceUnits || "km";
    const [hoursStr, setHours] = useState<string>(hours ? hours.toString() : "0");
    const [minutesStr, setMinutes] = useState<string>(minutes ? minutes.toString() : "0");
    const [paceStrMinutes, setPaceStrMinutes] = useState<string>("0");
    const [paceStrSeconds, setPaceStrSeconds] = useState<string>("0");
    const [cutoff, setCutoff] = useState<dayjs.Dayjs | null>(section.cutOff ? dayjs(section.cutOff) : null);

    useEffect(() => {
        setCutoff(section.cutOff ? dayjs(section.cutOff) : null);
    }, [section]);

    useEffect(() => {
        setHours(hours ? hours.toString() : "0");
        setMinutes(minutes ? minutes.toString() : "0");

        const totalPaceInMinutes = +paceStrMinutes + (+paceStrSeconds / 60);
        const totalTimeInMinutes = convertDistance(section.distance / 1000, distanceUnits) * totalPaceInMinutes;
        const hoursFromPace = Math.floor(totalTimeInMinutes / 60);
        const minutesFromPace = Math.round(totalTimeInMinutes % 60);
        const runningPace = getPace(convertDistance(section.distance / 1000, distanceUnits), (hours || 0) * 60 + (minutes || 0)).split(":");

        if (hoursFromPace !== hours || minutesFromPace !== minutes) {
            setPaceStrMinutes((+runningPace[0]).toString());
            setPaceStrSeconds((+runningPace[1]).toString());
        }
    }, [hours, minutes, section.distance, distanceUnits]);

    const onChangeHoursStr = (hours: string) => {
        hours = hours.replace(/[^0-9]/g, ""); // Remove non-numeric characters
        if (hours === "") {
            setHours("");
            onChangeSectionTime(minutes ? +minutes : 0);
        } else {
            const hoursNum = parseInt(hours);
            if (!isNaN(hoursNum) && hoursNum >= 0) {
                setHours(hours);
                const totalMinutes = (hoursNum * 60) + (minutes ? +minutes : 0);
                onChangeSectionTime(totalMinutes);
            }
        }
    };

    const onChangeMinutesStr = (minutes: string) => {
        minutes = minutes.replace(/[^0-9]/g, ""); // Remove non-numeric characters
        if (+minutes > 59) {
            minutes = "59"; // Limit to 59 minutes
        }

        if (minutes === "") {
            setMinutes("");
            onChangeSectionTime(hours ? +hours * 60 : 0);
        } else {
            const minutesNum = parseInt(minutes);
            if (!isNaN(minutesNum) && minutesNum >= 0 && minutesNum < 60) {
                setMinutes(minutes);
                const totalMinutes = (hours ? +hours * 60 : 0) + minutesNum;
                onChangeSectionTime(totalMinutes);
            }
        }
    }

    const updateHoursAndMinutes = (paceMinutes: number, paceSeconds: number, distance: number) => {
        const totalPaceInMinutes = paceMinutes + (paceSeconds / 60);
        const totalTimeInMinutes = distance * totalPaceInMinutes;
        const hours = Math.floor(totalTimeInMinutes / 60);
        const minutes = Math.round(totalTimeInMinutes % 60);

        setHours(hours.toString());
        setMinutes(minutes.toString());
        onChangeSectionTime(hours * 60 + minutes);
    }

    const onChangePaceStrMinutes = (minutes: string) => {
        minutes = minutes.replace(/[^0-9]/g, ""); // Remove non-numeric characters
        if (+minutes > 59) {
            minutes = "59"; // Limit to 59 minutes
        }

        if (minutes === "") {
            setPaceStrMinutes("");
            updateHoursAndMinutes(0, +paceStrSeconds, convertDistance(section.distance / 1000, distanceUnits));
        } else {
            const minutesNum = parseInt(minutes);
            if (!isNaN(minutesNum) && minutesNum >= 0 && minutesNum < 60) {
                setPaceStrMinutes(minutes);
                updateHoursAndMinutes(minutesNum, +paceStrSeconds, convertDistance(section.distance / 1000, distanceUnits));
            }
        }
    }

    const onChangePaceStrSeconds = (seconds: string) => {
        seconds = seconds.replace(/[^0-9]/g, ""); // Remove non-numeric characters
        if (+seconds > 59) {
            seconds = "59"; // Limit to 59 seconds
        }

        if (seconds === "") {
            setPaceStrSeconds("");
            updateHoursAndMinutes(+paceStrMinutes, 0, convertDistance(section.distance / 1000, distanceUnits));
        } else {
            const secondsNum = parseInt(seconds);
            if (!isNaN(secondsNum) && secondsNum >= 0 && secondsNum < 60) {
                setPaceStrSeconds(seconds);
                updateHoursAndMinutes(+paceStrMinutes, secondsNum, convertDistance(section.distance / 1000, distanceUnits));
            }
        }
    }

    const sectionEndTime = totalTimeBefore + (hours || 0) * 60 + (minutes || 0);

    const timeToCuttoff = cutoff ? Math.round(cutoff.diff(dayjs(startDate).add(sectionEndTime, 'minute'), "minute")) : undefined;

    const scenarioControl = !readOnly && scenarioLabel !== undefined && onScenarioPercentageChange && onScenarioLockToggle ? (
        <PlannedTimeBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} flexWrap="wrap" marginY={1}>
            <Checkbox
                size="small"
                checked={scenarioLocked ?? true}
                onChange={(e) => onScenarioLockToggle(e.target.checked)}
                sx={{ p: 0.25 }}
                inputProps={{ 'aria-label': intl.formatMessage({ id: "plans.keepAs" }) }}
            />
            <Typography variant="body2">{intl.formatMessage({ id: "plans.keepAs" })}</Typography>
            <SmallTextField
                hiddenLabel
                variant="standard"
                size="small"
                type="number"
                value={scenarioPercentage ?? 10}
                onChange={(e) => onScenarioPercentageChange(parseFloat(e.target.value))}
                inputProps={{ min: 0, style: { textAlign: "center" } }}
            />
            <Typography variant="body2">{scenarioLabel}</Typography>
        </PlannedTimeBox>
    ) : null;

    return (
        <Box p={1}>
            {section.type !== "start" && section.type !== "end" && (
                <PlannedTimeBox display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                    <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: section.type === "control" ? "data.plannedTimeCPTitle" : "data.plannedTimeForSectionTitle" })}</Typography>
                    {readOnly ? (
                        <Typography variant="body2" fontWeight="bold">{hoursStr}h {minutesStr}min</Typography>
                    ) : (
                        <Box display="flex" alignItems="baseline">
                            <SmallTextField hiddenLabel variant="standard" size="small" type="text" inputMode="numeric" value={hoursStr} onChange={(ev) => onChangeHoursStr(ev.target.value)} /><Typography variant="body2" marginRight={1}>h:</Typography>
                            <SmallTextField hiddenLabel variant="standard" size="small" type="text" inputMode="numeric" value={minutesStr} onChange={(ev) => onChangeMinutesStr(ev.target.value)} /><Typography variant="body2">min</Typography>
                        </Box>
                    )}
                </PlannedTimeBox>
            )}
            {section.type === "control" && scenarioControl}
            {
                (section.type === "control" || section.type === "end") && !(readOnly && !cutoff) && (
                    <CuttoffTimeBox>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.cutoff" })}</Typography>
                            {readOnly ? (
                                <Typography variant="body2">{cutoff ? cutoff.format('MMM D, HH:mm') : '—'}</Typography>
                            ) : (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimePicker
                                        value={cutoff}
                                        onChange={(newValue) => {
                                            const validatedValue = newValue && newValue.isValid() ? newValue : null;
                                            setCutoff(validatedValue);
                                            onChangeCutoff(validatedValue?.toDate() || null)
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                size: 'small',
                                                sx: { "& input": { fontSize: "0.875rem" } }
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            )}
                        </Box>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.timeToCuttoff" })}</Typography>
                            <ParagraphLine />
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight="bold" color={
                                    timeToCuttoff && timeToCuttoff < 0 ? "error.main" : timeToCuttoff && timeToCuttoff > 10 ? "success.main" :
                                        ((timeToCuttoff && timeToCuttoff <= 10) ||
                                            timeToCuttoff === 0) ? "warning.main" : "inherit"
                                }>{
                                        timeToCuttoff === 0 ?
                                            `${timeToCuttoff}min` :
                                            timeToCuttoff && timeToCuttoff > 0 ?
                                                `+${timeToCuttoff}min` :
                                                timeToCuttoff && timeToCuttoff < 0 ?
                                                    `${timeToCuttoff}min` : ""
                                    }</Typography>

                                {timeToCuttoff && timeToCuttoff < 0 ? <ErrorOutline fontSize="small" color="error" /> : timeToCuttoff && timeToCuttoff > 10 ? <CheckOutlined fontSize="small" color="success" /> :
                                    ((timeToCuttoff && timeToCuttoff <= 10) ||
                                        timeToCuttoff === 0) ? <WarningOutlined fontSize="small" color="warning" /> : null}
                            </Box>
                        </Box>
                    </CuttoffTimeBox>
                )
            }
            {section.type === "section" && sport === "bike" && (
                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                    <Typography variant="body2">{intl.formatMessage({ id: sport === "bike" ? "data.averageSpeedForSection" : "data.averagePaceForSection" })}</Typography>
                    <ParagraphLine />
                    <Typography variant="body2" fontWeight="bold">{getBikePace(convertDistance(section.distance / 1000, distanceUnits), (hours || 0) * 60 + (minutes || 0))} {distanceUnits === "km" ? "km" : "miles"}/h</Typography>
                </Box>
            )}
            {section.type === "section" && sport === "run" && (
                <PlannedTimeBox display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                    <Typography variant="body2" fontWeight="bold">{intl.formatMessage({ id: "data.averagePaceForSection" })}</Typography>
                    {readOnly ? (
                        <Typography variant="body2" fontWeight="bold">{paceStrMinutes}:{paceStrSeconds.padStart(2, '0')} min/{distanceUnits === "km" ? "km" : "mile"}</Typography>
                    ) : (
                        <Box display="flex" alignItems="baseline">
                            <SmallTextField hiddenLabel variant="standard" size="small" type="text" inputMode="numeric" value={paceStrMinutes} onChange={(ev) => onChangePaceStrMinutes(ev.target.value)} /><Typography variant="body2">:</Typography>
                            <SmallTextField hiddenLabel variant="standard" size="small" type="text" inputMode="numeric" value={paceStrSeconds} onChange={(ev) => onChangePaceStrSeconds(ev.target.value)} /><Typography variant="body2">min/{distanceUnits === "km" ? "km" : "mile"}</Typography>
                        </Box>
                    )}
                </PlannedTimeBox>
            )}
            {section.type === "section" && scenarioControl}
            {
                section.type !== "start" && section.type !== "end" && (
                    <>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: sport === "bike" ? "data.averageSpeedForSectionEnd" : "data.averagePaceForSectionEnd" })}</Typography>
                            <ParagraphLine />
                            {
                                sport === "bike" ?
                                    <Typography variant="body2" fontWeight="bold">{getBikePace(convertDistance(section.endDistance / 1000, distanceUnits), sectionEndTime)} {distanceUnits === "km" ? "km" : "miles"}/h</Typography> :
                                    <Typography variant="body2" fontWeight="bold">{getPace(convertDistance(section.endDistance / 1000, distanceUnits), sectionEndTime)} min/{distanceUnits === "km" ? "km" : "mile"}</Typography>
                            }
                        </Box>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                            <Typography variant="body2">{intl.formatMessage({ id: "data.finishTimeForSection" })}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2" fontWeight="bold">{toArrivalTime(startDate, sectionEndTime)}</Typography>
                        </Box>
                    </>
                )
            }
            < Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1} >
                <Typography variant="body2">{intl.formatMessage({ id: "data.totalPlannedTime" })}</Typography>
                <ParagraphLine />
                <Typography variant="body2" fontWeight="bold">{minutesToHHMM(totalTime)}</Typography>
            </Box >
            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                <Typography variant="body2">{intl.formatMessage({ id: sport === "bike" ? "data.totalAverageSpeed" : "data.totalAveragePace" })}</Typography>
                <ParagraphLine />
                {
                    sport === "bike" ?
                        <Typography variant="body2" fontWeight="bold">{getBikePace(convertDistance(totalDistance, distanceUnits), totalTime)} {distanceUnits === "km" ? "km" : "miles"}/h</Typography> :
                        <Typography variant="body2" fontWeight="bold">{getPace(convertDistance(totalDistance, distanceUnits), totalTime)} min/{distanceUnits === "km" ? "km" : "mile"}</Typography>
                }
            </Box>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1}>
                <Typography variant="body2">{intl.formatMessage({ id: "data.totalFinishTime" })}</Typography>
                <ParagraphLine />
                <Typography variant="body2" fontWeight="bold">{toArrivalTime(startDate, totalTime)}</Typography>
            </Box>
            {
                section.type === "start" && (
                    <Alert severity="info">{intl.formatMessage({ id: "data.timeboxInfo" })}</Alert>
                )
            }
        </Box >
    );
});

export default TimeBox;